import React, { useCallback, useReducer, useMemo, useState } from 'react';

import { Stack } from 'ui/stack';

import TabHeader from 'components/partials/tab-header';
import TableComponent from 'components/partials/table-component/table-component';
import EmptySpaceContent from 'components/partials/empty-space-content/empty-space-content';
import { Loader } from 'ui/loader';
import SegmentedTab from 'components/partials/segmented-tab/segmented-tab';
import type { SegmentedTabOption } from 'components/partials/segmented-tab/segmented-tab';

import { ArchiveModelModal } from './components/archive-model-modal';
import { CopyModelModal } from './aid-studio-details/components/model-modals';

import { getAidStudioTableSettings } from './aid-studio-table-settings';

import { useAidModelsList } from './hooks/use-aid-models';
import { useModelArchive } from './hooks/use-model-archive';
import {
  getCopyModalHeader,
  useModelCopy,
} from './aid-studio-details/hooks/use-model-copy';
import { useCurrentUser } from 'hooks/api/use-current-user';

import { usersReducer } from 'containers/admin/reducer';
import { SEARCH_CHANGE, PAGE_CHANGE } from 'containers/admin/actions';

import { getCloneLandingPath } from './aid-studio-details/utils/aid-studio-paths';

import { MODELS_LIST_TAB } from './aid-studio.constants';

import type { ModelsListTab } from './aid-studio.constants';
import type { AidModelDto } from 'api/generated/schemas/aidModelDto';

import Icons from 'assets/icons';

const MODELS_LIST_TAB_OPTIONS: SegmentedTabOption<ModelsListTab>[] = [
  { value: MODELS_LIST_TAB.ALL, label: 'Active' },
  { value: MODELS_LIST_TAB.ARCHIVED, label: 'Archived' },
];

const initialState = {
  search: '',
  pageIndex: 0,
};

interface AidStudioTabProps {
  clientId: number;
  isUserView?: boolean;
}

interface AidModelsListResponse {
  data?: AidModelDto[];
  pagination?: { pageSize: number; totalRecords: number };
}

const getEmptyText = (search: string, activeTab: ModelsListTab): string => {
  if (search) return 'Sorry, no results found. Please try a different keyword.';

  return activeTab === MODELS_LIST_TAB.ARCHIVED
    ? 'There are no archived models'
    : 'There are no models added yet';
};

const AidStudioTab = ({
  clientId,
  isUserView,
}: AidStudioTabProps): React.JSX.Element => {
  const currentUser = useCurrentUser() as unknown as {
    data?: { oktaUserId?: string };
  };
  const userId = currentUser?.data?.oktaUserId;

  const [state, dispatch] = useReducer(usersReducer, initialState);

  const [{ pageSize, totalRecords }, setAmount] = useState({
    pageSize: 10,
    totalRecords: 0,
  });

  const [sortingParams, setSortingParams] = useState([
    { id: 'name', desc: false },
  ]);

  const [activeTab, setActiveTab] = useState<ModelsListTab>(
    MODELS_LIST_TAB.ALL
  );
  const [cloneSourceId, setCloneSourceId] = useState<number | null>(null);

  const { search, pageIndex } = state as {
    search: string;
    pageIndex: number;
  };

  const queryParams = useMemo(
    () => ({
      pageIndex,
      pageSize,
      sortBy: sortingParams,
      filters: {
        name: search ? { contains: search } : undefined,
        archived: activeTab === MODELS_LIST_TAB.ARCHIVED,
      },
    }),
    [pageIndex, pageSize, sortingParams, search, activeTab]
  );

  const aidModelsQuery = useAidModelsList({
    userId: userId ?? '',
    clientId,
    queryParams: queryParams as never,
    enabled: !!userId && !!clientId,
  });
  const response = aidModelsQuery.data as AidModelsListResponse | undefined;
  const isLoading = aidModelsQuery.isLoading;
  const isFetching = aidModelsQuery.isFetching;
  const isError = aidModelsQuery.isError;

  const data = response?.data || [];
  const paginationData = response?.pagination;

  React.useEffect(() => {
    if (paginationData) {
      setAmount({
        pageSize: paginationData.pageSize,
        totalRecords: paginationData.totalRecords,
      });
    }
  }, [paginationData]);

  const handleSearch = useCallback(
    (payload: string): void => dispatch({ type: SEARCH_CHANGE, payload }),
    []
  );

  const handlePagination = useCallback(
    (payload: number): void => dispatch({ type: PAGE_CHANGE, payload }),
    []
  );

  const handleTabSelect = useCallback(
    (tab: ModelsListTab): void => {
      setActiveTab(tab);
      handlePagination(0);
    },
    [handlePagination]
  );

  const handleArchived = useCallback((): void => {
    if (data.length <= 1 && pageIndex > 0) {
      handlePagination(pageIndex - 1);
    }
  }, [data.length, pageIndex, handlePagination]);

  const handleRestored = useCallback(
    (): void => handleTabSelect(MODELS_LIST_TAB.ALL),
    [handleTabSelect]
  );

  const { requestArchive, restore, ...archive } = useModelArchive({
    userId: userId ?? '',
    clientId,
    onArchived: handleArchived,
    onRestored: handleRestored,
  });

  const modelCopy = useModelCopy({
    userId: userId ?? '',
    clientId,
    sourceModelId: cloneSourceId ?? undefined,
    landingPath: (newModelId) =>
      getCloneLandingPath(clientId, newModelId, isUserView),
    hasUnsavedChanges: false,
  });

  const { openSaveAsNewVersion, closeModal: closeCloneModal } = modelCopy;

  const rowActions = useMemo(
    () => ({
      onClone: (model: AidModelDto): void => {
        setCloneSourceId(model.id ?? null);
        openSaveAsNewVersion();
      },
      onArchive: (model: AidModelDto): void => {
        if (model.id != null) requestArchive(model.id);
      },
      onRestore: (model: AidModelDto): void => {
        if (model.id != null) void restore(model.id);
      },
    }),
    [openSaveAsNewVersion, requestArchive, restore]
  );

  const handleCloseCloneModal = useCallback((): void => {
    closeCloneModal();
    setCloneSourceId(null);
  }, [closeCloneModal]);

  const columns = useMemo(
    () => getAidStudioTableSettings(clientId, rowActions, isUserView),
    [clientId, rowActions, isUserView]
  );

  const tabs = (
    <SegmentedTab
      label="Models list filter"
      options={MODELS_LIST_TAB_OPTIONS}
      value={activeTab}
      onChange={handleTabSelect}
      className="w-fit"
      data-testid="models-list-tabs"
    />
  );

  const initialLoading = isLoading && !response;
  const tableLoading = isFetching && !initialLoading;

  const renderContent = (): React.JSX.Element => {
    if (initialLoading) {
      return (
        <Loader
          loading={true}
          backdrop="white"
          fullScreen={false}
          overlay="transparent"
        />
      );
    }

    if (!!data.length || tableLoading) {
      return (
        <TableComponent
          columns={columns}
          data={data}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPagination={handlePagination}
          setAmount={setAmount}
          sortingParams={sortingParams}
          setSortingParams={setSortingParams}
          totalRecords={totalRecords}
          isLoading={tableLoading}
          customHeight="max-h-[50vh]"
          toolbarStart={tabs}
        />
      );
    }

    const emptyImage = <Icons.EmptyImageAll customClass="size-20" />;

    if (isError) {
      return (
        <>
          {tabs}
          <EmptySpaceContent
            customClass="mt-4 flex-1"
            image={emptyImage}
            text="An error occurred while loading data. Please try again."
          />
        </>
      );
    }

    return (
      <>
        {tabs}
        <EmptySpaceContent
          customClass="mt-4 flex-1"
          image={emptyImage}
          text={getEmptyText(search, activeTab)}
        />
      </>
    );
  };

  return (
    <Stack sx={{ flex: '1 1 0%' }}>
      <TabHeader
        title="Aid Studio"
        search={search}
        onSearchChange={handleSearch}
        searchVariant="full"
        actionButton={
          isUserView
            ? null
            : {
                label: 'Add new model',
                to: `/admin/clients/${clientId}/aid-studio/new`,
                testId: 'add-aid-model-button',
              }
        }
        customClass="!mb-0"
      />

      {renderContent()}

      {archive.isArchiveModalOpen && (
        <ArchiveModelModal
          loading={archive.isUpdating}
          onConfirm={archive.confirmArchive}
          onClose={archive.cancelArchive}
        />
      )}

      {modelCopy.mode && (
        <CopyModelModal
          headerText={getCopyModalHeader(modelCopy.mode)}
          onConfirm={modelCopy.handleCopy}
          onClose={handleCloseCloneModal}
        />
      )}
    </Stack>
  );
};

export default AidStudioTab;
